import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { useCart } from './Cart';
import productService from '../services/productService';
import './ProductGrid.css';

// Fonction utilitaire pour obtenir la valeur de couleur
const getColorValue = (colorName) => {
  const colors = {
    'Bleu Titane': '#5e8ca5',
    'Noir Titane': '#444',
    'Blanc Titane': '#f0f0f0',
    'Or': '#f5d2a0',
    'Argent': '#e0e0e0',
    'Graphite': '#4d4d4d',
    'Bleu': '#0071e3',
    'Rose': '#f8b6c1',
    'Minuit': '#1a1a1a',
    'Starlight': '#f5f5f7',
    'Rouge': '#ff3b30',
    'Vert': '#34c759',
    'Violet': '#af52de',
    'Jaune': '#ffcc00'
  };
  return colors[colorName] || '#cccccc';
};

// Fonction utilitaire pour les images par défaut
const getDefaultImage = (product) => {
  // Utiliser la première image de la galerie si disponible
  if (product.image) {
    return `https://inza-backend.sajholding.org${product.image}`;
  }
  
  // Fallback vers une image par défaut locale
  return '/api/placeholder/300/300';
};

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mainProduct, setMainProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  
  const { addToCart, updateQuantity, getQuantity } = useCart();
  
  // Charger les produits depuis l'API
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const data = await productService.getProducts();
        setProducts(data);
        if (data.length > 0 && !mainProduct) {
          setMainProduct(data[0]);
        }
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Impossible de charger les produits');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Définir le premier produit comme produit principal par défaut
  useEffect(() => {
    if (products.length > 0 && !mainProduct) {
      setMainProduct(products[0]);
    }
  }, [products, mainProduct]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setMainProduct(product);
    const firstColor = product.colors && product.colors.length > 0 ? product.colors[0].name : '';
    setSelectedColor(firstColor);
    setQuantity(1);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    document.body.style.overflow = 'auto';
  };

  const handleAddToCart = useCallback(() => {
    if (!selectedProduct) return;
    
    // Préparer l'objet de configuration avec storage et color pour l'API
    const configuration = {
      storage: selectedProduct.storage_options && selectedProduct.storage_options.length > 0 ? selectedProduct.storage_options[0].name : 'default',
      color: selectedColor
    };
    
    // Appeler addToCart avec les paramètres attendus
    addToCart(selectedProduct, configuration);
    setNotificationMessage(`${selectedProduct.name} ajouté au panier`);
    setShowNotification(true);
    
    // Masquer la notification après 3 secondes
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
    
    // Réinitialiser la quantité et fermer le modal
    setQuantity(1);
    closeModal();
  }, [selectedProduct, quantity, selectedColor, addToCart]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Mettre à jour la couleur sélectionnée
  const handleColorSelect = useCallback((color) => {
    setSelectedColor(color);
  }, []);

  // Gérer le changement de quantité
  const handleQuantityChange = useCallback((newQuantity) => {
    if (newQuantity < 1) return;
    setQuantity(newQuantity);
  }, []);

  // Debug: Vérifier l'état du modal
  console.log('Rendu du composant - isModalOpen:', isModalOpen, 'selectedProduct:', selectedProduct);


  return (
    <div className="product-showcase-container">
      <h2 className="section-title">Nos Téléphones</h2>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Chargement des produits...</div>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#ff6b6b' }}>
          <div>{error}</div>
        </div>
      ) : (
        <div className="product-showcase">
          {/* Produit principal */}
          {mainProduct && (
            <motion.div 
              className="main-product"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="main-product-image-container">
                <img 
                  src={getDefaultImage(mainProduct)} 
                  alt={mainProduct.name}
                  className="main-product-image"
                  onClick={() => handleProductClick(mainProduct)}
                />
                {mainProduct.is_new && <div className="main-product-badge">Nouveau</div>}
                <div className="main-product-info">
                  <div className="product-header">
                    <h3>{mainProduct.name}</h3>
                  </div>
                  <p className="main-product-price">{formatPrice(mainProduct.price)}</p>
                  <div className="main-product-storage">
                    {mainProduct.storage_options && mainProduct.storage_options.length > 0 ? mainProduct.storage_options[0].name : ''}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Grille des produits secondaires */}
          <div className="product-thumbnails">
            {products.map((product, index) => (
              <motion.div 
                key={product.id}
                className={`thumbnail-card ${mainProduct?.id === product.id ? 'active' : ''}`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => setMainProduct(product)}
              >
                <div className="thumbnail-image-container">
                  <img 
                    src={getDefaultImage(product)} 
                    alt={product.name} 
                    className="thumbnail-image"
                    loading="lazy"
                  />
                </div>
                <div className="thumbnail-info">
                  <div className="product-header">
                    <span className="thumbnail-name">{product.name}</span>
                  </div>
                  <span className="thumbnail-price">{formatPrice(product.price)}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {isModalOpen && selectedProduct && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}>
              <button 
                onClick={closeModal}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
              
              <h2 style={{ marginTop: 0 }}>{selectedProduct.name}</h2>
              
              {/* Image du produit */}
              <div style={{ 
                margin: '0 auto 20px',
                maxWidth: '300px',
                textAlign: 'center'
              }}>
                <img 
                  src={getDefaultImage(selectedProduct)} 
                  alt={selectedProduct.name}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              
              <p>{selectedProduct.description}</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', margin: '20px 0' }}>
                {formatPrice(selectedProduct.price)}
              </p>
              
              {/* Sélecteur de quantité */}
              <div style={{ margin: '20px 0' }}>
                <p style={{ marginBottom: '10px' }}><strong>Quantité :</strong></p>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <button 
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '1px solid #d1d5db',
                      background: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1
                    }}
                    aria-label="Réduire la quantité"
                  >
                    -
                  </button>
                  
                  <span style={{ 
                    minWidth: '30px', 
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: '500'
                  }}>
                    {quantity}
                  </span>
                  
                  <button 
                    onClick={() => setQuantity(prev => prev + 1)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '1px solid #d1d5db',
                      background: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1
                    }}
                    aria-label="Augmenter la quantité"
                  >
                    +
                  </button>
                  
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      Total: {formatPrice(selectedProduct.price * quantity)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sélection de couleur */}
              {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                <div style={{ margin: '20px 0' }}>
                  <p style={{ marginBottom: '10px' }}><strong>Couleur :</strong> {selectedColor}</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {selectedProduct.colors.map((color) => (
                      <button
                        key={color.id}
                        onClick={() => setSelectedColor(color.name)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: color.code || '#cccccc',
                          border: selectedColor === color.name ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                          cursor: 'pointer',
                          padding: 0
                        }}
                        aria-label={`Couleur ${color.name}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              <button 
                onClick={handleAddToCart}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '14px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  marginTop: '24px',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                <ShoppingCart size={18} style={{ marginRight: '8px' }} />
                <span>Ajouter au panier - {formatPrice(selectedProduct.price * quantity)}</span>
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>
      
      {/* Notification d'ajout au panier */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{notificationMessage}</span>
        </div>
      )}
      
      <style jsx="true" global="true">{`
        @keyframes slideIn {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductGrid;
